import {
  IFormJBPHPContactChangeRequest,
  newHpContactRequest,
  JBPHPContactChangeRequestStatus as Status,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { HPContactsDuplicateDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsDuplicateDialog/HPContactsDuplicateDialog'
import { HpContactsTicketDeleteDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketDeleteDialog/HpContactsTicketDeleteDialog'
import { HpContactsTicketNotNewDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketNotNewDialog/HpContactsTicketNotNewDialog'
import { saveHPContactChangeRequest } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/saveHPContactChangeRequest'
import {
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { ComponentProps, createContext, ReactNode, Ref, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { guid } from 'core/classes/helpers/GuidHelper'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { Card } from 'partner360/components/card'
import { PLANNING_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/planningCockpitTourTargets'
import { HPContactRequestEditor } from 'partner360/features/hpContacts'
import { getContactsByEmail } from 'partner360/features/hpContacts/api/getContactsByEmail'
import { useLeadPbmId } from 'partner360/features/hpContacts/model'
import { getDefaultHpContactRole } from 'partner360/features/hpContacts/model/getDefaultHpContactRole'
import { getHPContactRoleForEditing } from 'partner360/features/hpContacts/model/getHPContactRoleForEditing'
import { removeHpContactRequest } from 'partner360/services/hpContacts/removeHpContactRequest'

import { CockpitCardHeader } from '../components/cockpitCardHeader'
import { CockpitIconButton } from '../components/cockpitIconButton'

import {
  SalesTeamContactActions,
  SalesTeamExpandedView,
  SalesTeamRequestStatus,
} from './components'
import { buildSalesTeamItems, ICockpitSalesTeamContact } from './helpers/buildSalesTeamItems'
import { useSalesTeamRequests } from './hooks/useSalesTeamRequests'
import {
  companySalesTeamTranslation as KEYS,
  salesTeamRequestTranslation as REQUEST_KEYS,
} from './translation'

import styles from './CockpitSalesTeam.module.scss'

// Keep the existing form intact; the surrounding fieldset also protects read-only requests.
const RequestDialogContent = ({
  readOnly,
  ...props
}: ComponentProps<typeof HPContactRequestEditor> & { readOnly: boolean }) => {
  return (
    <fieldset className={styles.formGuard} disabled={readOnly} inert={readOnly}>
      <HPContactRequestEditor {...props} />
    </fieldset>
  )
}

interface SalesTeamProviderProps {
  partnerId: string
  expanded: boolean
  onExpand: () => void
  onCollapse: () => void
  sourceRef?: Ref<HTMLElement>
  children: ReactNode
}

const SalesTeamContext = createContext<{
  collapsed: ReactNode
  expanded: ReactNode
  renderTitle: (onCollapse: () => void) => ReactNode
} | null>(null)

const useSalesTeamView = () => {
  const value = useContext(SalesTeamContext)
  if (!value) throw new Error('Sales Team views require CockpitSalesTeamProvider')
  return value
}

const CockpitSalesTeamProviderBody = ({
  partnerId,
  expanded,
  onExpand,
  onCollapse,
  sourceRef,
  children,
}: SalesTeamProviderProps) => {
  const t = useSafetyTranslation()
  const {
    items: requestRecords,
    loading: requestsLoading,
    error: requestsError,
    crud,
    refresh,
    fetchPartnerRoles,
    partnerRoles,
  } = useSalesTeamRequests(partnerId)
  const contentLoading = requestsLoading
  const { items, people } = useMemo(() => buildSalesTeamItems(requestRecords), [requestRecords])
  const [selectedRole, setSelectedRole] = useState('')
  const [requestReadOnly, setRequestReadOnly] = useState(false)
  const [request, setRequest] = useState<IFormJBPHPContactChangeRequest | null>(null)
  const [dialog, setDialog] = useState<'duplicate' | 'delete' | 'notNew' | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<IFormJBPHPContactChangeRequest | null>(null)
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [leadId] = useLeadPbmId({ partnerId })
  const { isPartnerUser } = useUserRoles()
  const editMode = useSelector(selectPublishingMode)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const programType = useSelector(selectProgramType)
  const user = useSelector(selectUser)
  const canEdit = editMode && !isPartnerUser
  const defaultRole = getDefaultHpContactRole({
    isDistributor,
    isRetailProgramType: programType === ProgramTypes.Retail,
  })
  // Legacy request defaults remain separate from the coverage team's display filter.
  const preferredRole =
    partnerRoles.find((role) => role.Roles === defaultRole)?.Roles || partnerRoles[0]?.Roles
  const currentRole =
    selectedRole === ''
      ? ''
      : items.some((item) => item.Roles === selectedRole) ||
          partnerRoles.some((role) => role.Roles === selectedRole)
        ? selectedRole
        : ''
  const editingRole = getHPContactRoleForEditing({
    isDistributor,
    currentDefaultHpContactRole: defaultRole,
    currentRole: expanded ? currentRole || preferredRole : preferredRole,
  })
  const [shown, setShown] = useState(0)
  const index = Math.min(shown, Math.max(0, items.length - 1))
  const agent = items[index]
  const requests = requestRecords.filter(
    (item) => item.TicketId > 0 && item.TicketStatus !== Status.Closed
  )
  const openRequest = (item: IFormJBPHPContactChangeRequest, readOnly = false) => {
    setRequestReadOnly(readOnly)
    setSaveError(false)
    setRequest(item)
  }
  const addRequest = () =>
    openRequest({
      ...newHpContactRequest,
      PartnerId: partnerId,
      FirstName: user.firstName,
      LastName: user.lastName,
      Email: user.email,
      SignId: `new-${guid()}`,
      Roles: editingRole,
      RequestAction: 'Add',
    })
  const closeRequest = () => {
    if (!busy) setRequest(null)
  }
  const canActivateRequest = (item: ICockpitSalesTeamContact) =>
    item.TicketId > 0 && canEdit && item.TicketStatus !== Status.WIP
  const activateRequest = (item: ICockpitSalesTeamContact) => {
    const request = item.managementContact || item
    if (busy || !canActivateRequest(request)) return
    setDeleteTarget({ ...request, RequestAction: 'DeleteTicket' })
    setDialog(request.TicketStatus === Status.New ? 'delete' : 'notNew')
  }
  const getMenuItems = (member: ICockpitSalesTeamContact) => {
    const agent = member.managementContact
    return agent
      ? [
          ...(agent.TicketId && agent.RequestAction !== 'Delete'
            ? [
                {
                  label: t(REQUEST_KEYS.viewRequest),
                  icon: 'kbase kbase-eye',
                  action: () => openRequest(agent, true),
                },
              ]
            : []),
          ...(!agent.TicketId && canEdit
            ? [
                {
                  label: t(REQUEST_KEYS.changeContact),
                  icon: 'kbase kbase-user-editor',
                  action: () => openRequest({ ...agent, RequestAction: 'Update' }),
                },
                {
                  label: t(REQUEST_KEYS.removeContact),
                  destructive: true,
                  icon: 'kbase kbase-delete',
                  action: () => openRequest({ ...agent, RequestAction: 'Delete' }),
                },
              ]
            : []),
          ...(agent.TicketId && canEdit && agent.TicketStatus !== Status.WIP
            ? [
                {
                  label: t(REQUEST_KEYS.deleteRequest),
                  destructive: true,
                  icon: 'kbase kbase-close',
                  action: () => {
                    setDeleteTarget({ ...agent, RequestAction: 'DeleteTicket' })
                    setDialog(agent.TicketStatus === Status.New ? 'delete' : 'notNew')
                  },
                },
              ]
            : []),
        ]
      : []
  }

  const collapsedView = (
    <Card
      ref={sourceRef}
      className={styles.root}
      bodyClassName={styles.body}
      hoverable={false}
      aria-label={t(KEYS.title)}
      data-tour-id={TOUR_TARGETS.salesTeam}
      header={
        <CockpitCardHeader
          title={<Translation text={KEYS.title} />}
          actions={
            <>
              <CockpitIconButton
                icon={expanded ? 'kbase kbase-compress' : 'kbase kbase-expand'}
                label={t(expanded ? REQUEST_KEYS.collapse : REQUEST_KEYS.expand)}
                onClick={expanded ? onCollapse : onExpand}
              />
              {canEdit && (
                <CockpitIconButton
                  icon="hpicon hpicon-add-contact"
                  label={t(REQUEST_KEYS.addContact)}
                  disabled={requestsLoading || requestsError || busy}
                  onClick={addRequest}
                />
              )}
            </>
          }
          description={
            contentLoading || busy ? (
              t(busy ? REQUEST_KEYS.saving : REQUEST_KEYS.loading)
            ) : (
              <>
                <span className={styles.figure}>{people.length}</span>{' '}
                {t(people.length === 1 ? REQUEST_KEYS.person : REQUEST_KEYS.people)}
                {requests.length > 0 && (
                  <>
                    {' '}
                    · <span className={styles.figure}>{requests.length}</span>{' '}
                    {t(REQUEST_KEYS.openRequests)}
                  </>
                )}
              </>
            )
          }
        />
      }
    >
      {contentLoading || busy ? (
        <div
          className={styles.state}
          role="status"
          aria-label={t(busy ? REQUEST_KEYS.saving : REQUEST_KEYS.loading)}
        >
          <Loader dimension={{ width: 32, height: 32 }} />
        </div>
      ) : !agent ? (
        <div className={styles.state}>
          <span>
            <Translation text={KEYS.emptySalesTeamContent} />
          </span>
        </div>
      ) : (
        <>
          <div className={styles.identity}>
            <div className={styles.name}>
              {[agent.FirstName, agent.LastName].filter(Boolean).join(' ') ||
                t(REQUEST_KEYS.noValue)}
            </div>
            {!!agent.LeadPBM && (
              <span className={styles.lead}>
                <i className="kbase kbase-star-alt" aria-hidden />{' '}
                <Translation text={KEYS.leadPBM} />
              </span>
            )}
          </div>
          <div className={styles.role}>{agent.Roles || t(REQUEST_KEYS.noValue)}</div>
          <dl className={styles.facts}>
            <div>
              <dt>
                <Translation text={KEYS.bUnit} />
              </dt>
              <dd>{agent.BusinessUnit?.split('/').join(', ') || t(REQUEST_KEYS.noValue)}</dd>
            </div>
            <div>
              <dt>
                <Translation text={KEYS.assignmentPeriod} />
              </dt>
              <dd>{agent.AssignmentPeriod || t(REQUEST_KEYS.noValue)}</dd>
            </div>
          </dl>
          <SalesTeamRequestStatus
            item={agent}
            busy={busy}
            actionable={canActivateRequest(agent)}
            onClick={() => activateRequest(agent)}
          />
          <div className={styles.footer}>
            <SalesTeamContactActions
              email={agent.Email}
              menuItems={getMenuItems(agent)}
              disabled={busy}
            />
            <div className={styles.navigation}>
              <CockpitIconButton
                icon="kbase kbase-chevron-left"
                label={t(REQUEST_KEYS.previousContact)}
                disabled={items.length <= 1 || busy}
                onClick={() => setShown((index - 1 + items.length) % items.length)}
              />
              <span>
                <span className={styles.figure}>{index + 1}</span> {t(REQUEST_KEYS.of)}{' '}
                <span className={styles.figure}>{items.length}</span>
              </span>
              <CockpitIconButton
                icon="kbase kbase-chevron-right"
                label={t(REQUEST_KEYS.nextContact)}
                disabled={items.length <= 1 || busy}
                onClick={() => setShown((index + 1) % items.length)}
              />
            </div>
          </div>
        </>
      )}
      {requestsError && (
        <div className={styles.state} role="alert">
          <Translation text={REQUEST_KEYS.requestsLoadError} />
          <CockpitIconButton
            icon="kbase kbase-reload"
            label={t(REQUEST_KEYS.retry)}
            showLabel
            onClick={refresh}
          />
        </div>
      )}
      {saveError && (
        <div role="alert">
          <Translation text={REQUEST_KEYS.saveError} />
        </div>
      )}
    </Card>
  )
  const expandedView = (
    <SalesTeamExpandedView
      {...{
        items,
        loading: contentLoading,
        requestsLoading,
        error: false,
        requestsError,
        saveError,
        refresh,
        getContactMenuItems: getMenuItems,
        busy,
        onOpenRequest: activateRequest,
        canOpenRequest: canActivateRequest,
        role: currentRole || '',
        onRoleChange: setSelectedRole,
        partnerRoles,
      }}
    />
  )
  const renderExpandedTitle = (collapse: () => void) => (
    <CockpitCardHeader
      title={<Translation text={KEYS.title} />}
      description={
        <>
          {items.find((item) => item.PartnerName)?.PartnerName && (
            <span>{items.find((item) => item.PartnerName)?.PartnerName} · </span>
          )}
          <span className={styles.figure}>{people.length}</span> {t(REQUEST_KEYS.people)} ·{' '}
          <span className={styles.figure}>{requests.length}</span> {t(REQUEST_KEYS.openRequests)}
        </>
      }
      actions={
        <>
          {canEdit && (
            <CockpitIconButton
              icon="hpicon hpicon-add-contact"
              label={t(REQUEST_KEYS.addContact)}
              showLabel
              className={styles.expandedAdd}
              disabled={requestsLoading || requestsError || busy}
              onClick={addRequest}
            />
          )}
          <CockpitIconButton
            icon="kbase kbase-compress"
            label={t(REQUEST_KEYS.collapse)}
            onClick={collapse}
          />
        </>
      }
    />
  )
  return (
    <SalesTeamContext.Provider
      value={{ collapsed: collapsedView, expanded: expandedView, renderTitle: renderExpandedTitle }}
    >
      {children}
      <ModalDialog
        show={Boolean(request)}
        size="sm"
        content={RequestDialogContent}
        contentProps={{
          isVisible: Boolean(request),
          readOnly: !canEdit || requestReadOnly,
          currentHpContactRequest: request || newHpContactRequest,
          partnerId,
          leadId,
          currentRole: editingRole,
          onCancel: closeRequest,
          onApply: async (newRequest: IFormJBPHPContactChangeRequest) => {
            if (busy || !canEdit || requestReadOnly) return
            // The legacy table closes the editor before submitting the request.
            setRequest(null)
            setBusy(true)
            setSaveError(false)
            try {
              const result = await saveHPContactChangeRequest({
                newRequest,
                resetCrudStorage: () => crud.removeQueries(true),
                getContactsByEmail: (email) =>
                  getContactsByEmail({ partnerId, email, isForce: true }),
                jbpHpContactChangeRequestCrud: crud,
                fetchPartnerRoles,
              })
              if (result.hasDuplicate) setDialog('duplicate')
              else {
                refresh()
              }
            } catch {
              setSaveError(true)
            } finally {
              setBusy(false)
            }
          },
        }}
        onClose={closeRequest}
      />
      <HPContactsDuplicateDialog isShow={dialog === 'duplicate'} onClose={() => setDialog(null)} />
      <HpContactsTicketNotNewDialog isShow={dialog === 'notNew'} onClose={() => setDialog(null)} />
      <HpContactsTicketDeleteDialog
        isShow={dialog === 'delete'}
        onClose={() => setDialog(null)}
        handleDelete={async () => {
          if (!deleteTarget || !canEdit || deleteTarget.TicketStatus !== Status.New || busy) return
          setDialog(null)
          setBusy(true)
          setSaveError(false)
          try {
            crud.removeQueries(true)
            await removeHpContactRequest({ crud, entryId: deleteTarget.TicketId.toString() })
            refresh()
            await fetchPartnerRoles(true)
          } catch {
            setSaveError(true)
          } finally {
            setBusy(false)
          }
        }}
      />
    </SalesTeamContext.Provider>
  )
}

export const CockpitSalesTeamProvider = (props: SalesTeamProviderProps) => (
  <CockpitSalesTeamProviderBody key={props.partnerId} {...props} />
)

export const CockpitSalesTeam = () => <>{useSalesTeamView().collapsed}</>
export const CockpitSalesTeamExpanded = () => <>{useSalesTeamView().expanded}</>
export const CockpitSalesTeamExpandedTitle = ({ onCollapse }: { onCollapse: () => void }) => (
  <>{useSalesTeamView().renderTitle(onCollapse)}</>
)
CockpitSalesTeam.displayName = 'CockpitSalesTeam'
